'use client'

import * as React from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utils/utils'

const chipVariants = cva(
  'inline-flex items-center rounded-full border text-sm capitalize px-2.5 font-thin  py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    defaultVariants: {
      capitalize: true,
      color: 'default',
      rounded: 'full',
      variant: 'default'
    },
    variants: {
      capitalize: {
        false: '',
        true: 'capitalize'
      },
      color: {
        danger: 'bg-destructive text-destructive-foreground',
        default: '',
        info: 'bg-[#336A9D]/10 text-[#336A9D]',
        primary: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        success: 'bg-success text-success-foreground',
        warning: 'bg-warning text-warning-foreground'
      },
      rounded: {
        full: 'rounded-full',
        lg: 'rounded-lg',
        md: 'rounded-md',
        sm: 'rounded-sm'
      },
      size: {
        lg: 'text-lg',
        md: 'text-md',
        sm: 'text-sm',
        xs: 'text-xs'
      },
      variant: {
        default:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive-muted text-destructive ',
        flat: 'border-transparent bg-transparent',
        info: 'border-transparent bg-[#336A9D]/10 text-[#336A9D] hover:bg-info/80',
        outline: 'text-foreground',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        success:
          'border-transparent bg-success text-success-foreground hover:bg-success/80',
        warning:
          'border-transparent bg-warning dark:bg-warning/20 text-warning-foreground hover:bg-warning/80'
      }
    }
  }
)

export interface ChipProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    VariantProps<typeof chipVariants> {}

function Chip({
  capitalize,
  className,
  color,
  rounded,
  size,
  variant,
  ...props
}: ChipProps) {
  return (
    <div
      className={cn(
        chipVariants({ capitalize, color, rounded, size, variant }),
        className
      )}
      {...props}
    />
  )
}

export { Chip, chipVariants }
